import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import EquipmentItemPicker from "../../shared/equipmentCatalog/EquipmentItemPicker";
import EquipmentPublicCodeChip from "../../shared/equipmentCatalog/EquipmentPublicCodeChip";
import ContractorEquipmentCatalogApi from "../../../../../infrastructure/api/contractorEquipmentCatalog";

const sumEquipmentTotal = (equipments = []) =>
  equipments.reduce((sum, eq) => {
    const qty = Number(eq.quantity) > 0 ? Number(eq.quantity) : 0;
    const unit = Number(eq.unitPrice) || 0;
    const lineTotal = Number(eq.totalPrice);
    if (Number.isFinite(lineTotal) && lineTotal > 0) return sum + lineTotal;
    return sum + qty * unit;
  }, 0);

const syncPackageTotals = (equipments, laborCost) => {
  const equipmentTotal = sumEquipmentTotal(equipments);
  const labor = Number(laborCost) || 0;
  return {
    equipmentTotal,
    labor,
    priceWithEquipment: equipmentTotal + labor,
  };
};

const formatMoneyFa = (value) =>
  Number(value || 0).toLocaleString("fa-IR");

const validationSchema = Yup.object().shape({
  title: Yup.string().required("لطفا عنوان پکیج را وارد کنید"),
  plantCapacity: Yup.number().required("لطفا ظرفیت نیروگاه را انتخاب کنید"),
  phaseCount: Yup.number()
    .oneOf([1, 3], "نوع نیروگاه باید یک‌فاز یا سه‌فاز باشد")
    .required("لطفا نوع نیروگاه (یک‌فاز / سه‌فاز) را انتخاب کنید"),
  description: Yup.string(),
  priceWithEquipment: Yup.number()
    .min(0, "قیمت نمی‌تواند منفی باشد")
    .nullable()
    .test(
      "at-least-one-price",
      "حداقل یکی از قیمت‌ها (با تجهیزات یا دستمزد) باید وارد شود",
      function (value) {
        const { priceWithoutEquipment, equipments } = this.parent;
        const equipmentTotal = sumEquipmentTotal(equipments);
        return value > 0 || priceWithoutEquipment > 0 || equipmentTotal > 0;
      }
    ),
  priceWithoutEquipment: Yup.number()
    .min(0, "قیمت نمی‌تواند منفی باشد")
    .nullable()
    .test(
      "at-least-one-price",
      "حداقل یکی از قیمت‌ها (با تجهیزات یا دستمزد) باید وارد شود",
      function (value) {
        const { priceWithEquipment, equipments } = this.parent;
        const equipmentTotal = sumEquipmentTotal(equipments);
        return value > 0 || priceWithEquipment > 0 || equipmentTotal > 0;
      }
    ),
  deliveryFee: Yup.number()
    .min(0, "هزینه ارسال نمی‌تواند منفی باشد")
    .required("لطفا هزینه ارسال را وارد کنید"),
  priceValidUntil: Yup.mixed().required("تاریخ اعتبار قیمت الزامی است"),
  installationDurationDays: Yup.number()
    .min(1, "مدت اجرا باید حداقل ۱ روز باشد")
    .nullable(),
  warrantyMonths: Yup.number()
    .min(0, "مدت گارانتی نمی‌تواند منفی باشد")
    .nullable(),
  status: Yup.number().required("لطفا وضعیت پکیج را انتخاب کنید"),
});

const PackageModifyPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id !== "create";
  const [loading, setLoading] = useState(false);
  const [createUpdateLoading, setCreateUpdateLoading] = useState(false);
  const [packageData, setPackageData] = useState(null);

  // Get baseUrl from global config
  const baseUrl = window.globalConfig?.API_URL || "";

  // Helper function: تبدیل status از string به number (در صورتی که API string برگرداند)
  const mapStatusToNumber = (status) => {
    if (typeof status === 'number') return status;
    if (status === null || status === undefined) return 1;
    
    const statusMap = {
      'Draft': 1,
      'Active': 2,
      'Inactive': 3,
      'UnderReview': 4,
      'Rejected': 5,
      'Archived': 6,
    };
    
    return statusMap[status] || parseInt(status) || 1;
  };

  // API معمولاً enum ظرفیت را عددی (5/10/15/20) برمی‌گرداند؛ گاهی هم "P10KW"
  const mapPlantCapacityToNumber = (capacity) => {
    if (capacity === null || capacity === undefined || capacity === "") return "";
    if (typeof capacity === "number" && [5, 10, 15, 20].includes(capacity)) {
      return capacity;
    }
    const asNumber = Number(capacity);
    if ([5, 10, 15, 20].includes(asNumber)) return asNumber;

    const capacityMap = {
      P5KW: 5,
      P10KW: 10,
      P15KW: 15,
      P20KW: 20,
      "5": 5,
      "10": 10,
      "15": 15,
      "20": 20,
    };
    return capacityMap[capacity] ?? "";
  };

  // Static options
  const plantCapacityOptions = [
    { value: 5, label: "5 کیلووات", name: "5 کیلووات" },
    { value: 10, label: "10 کیلووات", name: "10 کیلووات" },
    { value: 15, label: "15 کیلووات", name: "15 کیلووات" },
    { value: 20, label: "20 کیلووات", name: "20 کیلووات" },
  ];

  const phaseCountOptions = [
    { value: 1, label: "یک‌فاز" },
    { value: 3, label: "سه‌فاز" },
  ];

  const mapPhaseCountToNumber = (phase) => {
    if (phase === null || phase === undefined || phase === "") return "";
    if (phase === 1 || phase === 3) return phase;
    const asNumber = Number(phase);
    if (asNumber === 1 || asNumber === 3) return asNumber;
    const phaseMap = {
      SinglePhase: 1,
      ThreePhase: 3,
      "1": 1,
      "3": 3,
    };
    return phaseMap[phase] ?? "";
  };

  // Labels match common contractor price-list / BOM wording
  const equipmentTypes = [
    { value: 1, label: "ماژول فتوولتائیک (پنل)", name: "ماژول فتوولتائیک (پنل)" },
    { value: 2, label: "اینورتر", name: "اینورتر" },
    { value: 3, label: "سازه نگهدارنده (استراکچر)", name: "سازه نگهدارنده (استراکچر)" },
    { value: 4, label: "کابل AC / DC", name: "کابل AC / DC" },
    { value: 5, label: "کمباینر باکس", name: "کمباینر باکس" },
    { value: 6, label: "جانکشن باکس / تابلو AC", name: "جانکشن باکس / تابلو AC" },
    { value: 7, label: "تابلو DC", name: "تابلو DC" },
    { value: 8, label: "سیستم ارتینگ", name: "سیستم ارتینگ" },
    { value: 9, label: "مانیتورینگ / دیتالاگر", name: "مانیتورینگ / دیتالاگر" },
    { value: 10, label: "باتری", name: "باتری" },
    { value: 11, label: "شارژ کنترلر", name: "شارژ کنترلر" },
    { value: 99, label: "لوله فلکسیبل و سایر", name: "لوله فلکسیبل و سایر" },
  ];

  const MANDATORY_CATALOG_MESSAGE =
    "تمام تجهیزات پکیج باید از بانک مرجع تجهیزات انتخاب شوند.";

  const requiresCatalogSelection = () => true;

  // Load data on mount
  useEffect(() => {
    if (isEditMode) {
      fetchPackageDetails();
    }
  }, [id, isEditMode]);

  const fetchPackageDetails = async () => {
    setLoading(true);
    try {
      const response = await DefaultApiService.getApi(
        `SLN/Contractor/PackageManagement/GetById`,
        { id: id }
      );
      if (response?.isSuccess && response?.data) {
        const pkgData = response.data.package || response.data.Package || response.data;
        console.log("🔍 Package Data Loaded:", {
          status: pkgData.status,
          statusType: typeof pkgData.status,
          fullData: pkgData
        });
        setPackageData(pkgData);
      } else {
        toast.error("خطا در بارگذاری اطلاعات پکیج");
      }
    } catch (error) {
      toast.error("خطا در بارگذاری اطلاعات پکیج");
    } finally {
      setLoading(false);
    }
  };

  const onNavigateToListPage = () => {
    navigate("/SLN/Contractor/PackageManagement");
  };

  const onSubmit = async (formValue, { setSubmitting, setTouched }) => {
    setCreateUpdateLoading(true);
    
    // Validation برای تجهیزات
    if (formValue.equipments && formValue.equipments.length > 0) {
      const invalidEquipment = formValue.equipments.find(
        (eq) => !eq.equipmentType || !eq.brand || !eq.model
      );
      if (invalidEquipment) {
        toast.error("لطفا تمام فیلدهای الزامی تجهیزات (نوع، برند، مدل) را پر کنید");
        setTouched({ equipments: true });
        setCreateUpdateLoading(false);
        setSubmitting(false);
        return;
      }

      const missingCatalogEquipment = formValue.equipments.find(
        (eq) => !eq.equipmentItemId
      );
      if (missingCatalogEquipment) {
        toast.error(MANDATORY_CATALOG_MESSAGE);
        setTouched({ equipments: true });
        setCreateUpdateLoading(false);
        setSubmitting(false);
        return;
      }
    }

    // Validation برای ویژگی‌ها
    if (formValue.features && formValue.features.length > 0) {
      const invalidFeature = formValue.features.find(
        (ft) => !ft.featureTitle || !ft.featureValue
      );
      if (invalidFeature) {
        toast.error("لطفا تمام فیلدهای الزامی ویژگی‌ها (عنوان و مقدار) را پر کنید");
        setTouched({ features: true });
        setCreateUpdateLoading(false);
        setSubmitting(false);
        return;
      }
    }

    try {
      // تبدیل PlantCapacity از عدد به enum
      const plantCapacityEnum =
        formValue.plantCapacity === 5
          ? "P5KW"
          : formValue.plantCapacity === 10
          ? "P10KW"
          : formValue.plantCapacity === 15
          ? "P15KW"
          : formValue.plantCapacity === 20
          ? "P20KW"
          : "P5KW";

      const synced = syncPackageTotals(
        formValue.equipments,
        formValue.priceWithoutEquipment
      );

      // تبدیل داده‌ها به فرمت بک‌اند
      const payload = {
        packageId: isEditMode ? parseInt(id) : 0,
        title: formValue.title,
        plantCapacity: plantCapacityEnum,
        phaseCount: Number(formValue.phaseCount),
        description: formValue.description,
        priceWithEquipment:
          synced.equipmentTotal > 0 ? synced.priceWithEquipment : null,
        priceWithoutEquipment: synced.labor > 0 ? synced.labor : null,
        deliveryFee: parseFloat(formValue.deliveryFee) || 0,
        contractorPaysDelivery: false,
        priceValidUntil: formValue.priceValidUntil || null,
        installationDurationDays: formValue.installationDurationDays
          ? parseInt(formValue.installationDurationDays)
          : null,
        warrantyMonths: formValue.warrantyMonths
          ? parseInt(formValue.warrantyMonths)
          : null,
        status: formValue.status,
        isActive: formValue.isActive,
        imageUrl: formValue.imageUrl || null,
        equipments: (formValue.equipments || []).filter((eq) => eq.equipmentType && eq.brand && eq.model).map((eq) => ({
          equipmentType: parseInt(eq.equipmentType),
          brand: eq.brand,
          model: eq.model,
          quantity: parseInt(eq.quantity) || 1,
          unitPrice: parseFloat(eq.unitPrice) || 0,
          totalPrice: parseFloat(eq.totalPrice) || 0,
          specifications: eq.specifications || "",
          warrantyPeriod: eq.warrantyPeriod
            ? parseInt(eq.warrantyPeriod)
            : null,
          description: eq.description || "",
          imageUrl: eq.imageUrl || "",
          datasheetUrl: eq.datasheetUrl || "",
          equipmentItemId: eq.equipmentItemId || null,
          publicCode: eq.publicCode || "",
        })),
        features: (formValue.features || []).map((ft) => ({
          featureTitle: ft.featureTitle,
          featureValue: ft.featureValue,
          description: ft.description || "",
          displayOrder: parseInt(ft.displayOrder) || 0,
        })),
      };

      const response = await DefaultApiService.postApi(
        "SLN/Contractor/PackageManagement/Modify",
        payload
      );

      if (response?.isSuccess) {
        toast.success(
          isEditMode ? "پکیج با موفقیت ویرایش شد" : "پکیج با موفقیت ایجاد شد"
        );
        navigate("/SLN/Contractor/PackageManagement");
      } else {
        toast.error(response?.message || "خطایی رخ داده است.");
      }
    } catch (error) {
      toast.error(error?.message || "خطایی رخ داده است.");
    } finally {
      setCreateUpdateLoading(false);
      setSubmitting(false);
    }
  };

  // Initial values
  const initialValues =
    isEditMode && packageData
      ? {
          title: packageData.packageTitle || packageData.title || "",
          plantCapacity: mapPlantCapacityToNumber(
            packageData.plantCapacity ?? packageData.capacityType
          ),
          phaseCount: mapPhaseCountToNumber(
            packageData.phaseCount ?? packageData.PhaseCount
          ),
          description: packageData.description || "",
          priceWithEquipment: packageData.priceWithEquipment || "",
          priceWithoutEquipment: packageData.priceWithoutEquipment || "",
          deliveryFee: packageData.deliveryFee ?? 0,
          contractorPaysDelivery: packageData.contractorPaysDelivery || false,
          priceValidUntil:
            packageData.priceValidUntil || packageData.PriceValidUntil || "",
          installationDurationDays: packageData.installationDurationDays || "",
          warrantyMonths: packageData.warrantyMonths || "",
          status: mapStatusToNumber(packageData.status),
          isActive:
            packageData.isActive !== undefined ? packageData.isActive : true,
          imageUrl: packageData.imageUrl || "",
          equipments: (packageData.packageEquipments || packageData.equipments || []).map((eq) => ({
            equipmentType: eq.equipmentType || "",
            brand: eq.brand || "",
            model: eq.model || "",
            quantity: eq.quantity || 1,
            unitPrice: eq.unitPrice || 0,
            totalPrice: eq.totalPrice || 0,
            specifications: eq.specifications || "",
            warrantyPeriod: eq.warrantyPeriod || "",
            description: eq.description || "",
            imageUrl: eq.imageUrl || "",
            datasheetUrl: eq.datasheetUrl || "",
            equipmentItemId: eq.equipmentItemId || null,
            publicCode: eq.publicCode || "",
            masterPicker: eq.equipmentItemId
              ? {
                  equipmentItemId: eq.equipmentItemId,
                  publicCode: eq.publicCode,
                  brandName: eq.brand,
                  modelName: eq.model,
                  nameFa: eq.description || eq.model,
                }
              : null,
          })),
          features: (packageData.packageFeatures || []).map((ft) => ({
            featureTitle: ft.featureTitle || "",
            featureValue: ft.featureValue || "",
            description: ft.description || "",
            displayOrder: ft.displayOrder || 0,
          })),
        }
      : {
          title: "",
          plantCapacity: "",
          phaseCount: "",
          description: "",
          priceWithEquipment: "",
          priceWithoutEquipment: "",
          deliveryFee: 0,
          contractorPaysDelivery: false,
          priceValidUntil: "",
          installationDurationDays: "",
          warrantyMonths: "",
          status: 1,
          isActive: true,
          imageUrl: "",
          equipments: [],
          features: [],
        };

  if (loading && isEditMode) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: "60vh" }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={
              isEditMode
                ? `ویرایش ${packageData?.title || "پکیج"}`
                : "ایجاد پکیج جدید"
            }
          />

          <CardContent>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                  onBlur: handleBlur,
                });

                const pricing = syncPackageTotals(
                  values.equipments,
                  values.priceWithoutEquipment
                );
                const deliveryFee = Number(values.deliveryFee) || 0;
                const deliveryForCustomer = values.contractorPaysDelivery
                  ? 0
                  : deliveryFee;

                const updateEquipmentsAndTotals = (nextEquipments) => {
                  const next = syncPackageTotals(
                    nextEquipments,
                    values.priceWithoutEquipment
                  );
                  setFieldValue("equipments", nextEquipments);
                  setFieldValue(
                    "priceWithEquipment",
                    next.priceWithEquipment > 0 ? next.priceWithEquipment : ""
                  );
                };

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      {/* اطلاعات اصلی */}
                      <Grid item xs={12}>
                        <Typography variant="h6">اطلاعات اصلی پکیج</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          قیمت تجهیزات، دستمزد/اجرا و قیمت تمام‌شده به هم مرتبط‌اند:
                          جمع قیمت اقلام + دستمزد = قیمت تمام‌شده با تجهیزات.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="عنوان پکیج *"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("title")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={errors.plantCapacity && touched.plantCapacity}
                        >
                          <InputLabel>ظرفیت نیروگاه *</InputLabel>
                          <Select
                            name="plantCapacity"
                            label="ظرفیت نیروگاه *"
                            value={values.plantCapacity}
                            onChange={wrapFormikHandleChange(handleChange)}
                            onBlur={handleBlur}
                          >
                            {plantCapacityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label || option.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.plantCapacity && touched.plantCapacity && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                              {errors.plantCapacity}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={errors.phaseCount && touched.phaseCount}
                        >
                          <InputLabel>نوع نیروگاه *</InputLabel>
                          <Select
                            name="phaseCount"
                            label="نوع نیروگاه *"
                            value={values.phaseCount}
                            onChange={wrapFormikHandleChange(handleChange)}
                            onBlur={handleBlur}
                          >
                            {phaseCountOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.phaseCount && touched.phaseCount && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                              {errors.phaseCount}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl 
                          fullWidth 
                          variant="outlined"
                          error={errors.status && touched.status}
                        >
                          <InputLabel>وضعیت *</InputLabel>
                          <Select
                            name="status"
                            label="وضعیت *"
                            value={values.status}
                            onChange={wrapFormikHandleChange(handleChange)}
                            onBlur={handleBlur}
                          >
                            <MenuItem value={1}>پیش‌نویس</MenuItem>
                            <MenuItem value={2}>فعال</MenuItem>
                            <MenuItem value={3}>غیرفعال</MenuItem>
                            <MenuItem value={4}>در حال بررسی</MenuItem>
                            <MenuItem value={5}>رد شده</MenuItem>
                            <MenuItem value={6}>بایگانی</MenuItem>
                          </Select>
                          {errors.status && touched.status && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                              {errors.status}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.isActive}
                              onChange={(e) =>
                                setFieldValue("isActive", e.target.checked)
                              }
                              name="isActive"
                            />
                          }
                          label="فعال"
                          sx={{ m: 0 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="جمع تجهیزات (ریال)"
                          type="number"
                          value={pricing.equipmentTotal}
                          InputProps={{ readOnly: true }}
                          helperText="از جمع قیمت اقلام تجهیزات محاسبه می‌شود"
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="دستمزد / اجرا (ریال)"
                          type="number"
                          onChange={(e) => {
                            const labor = e.target.value;
                            setFieldValue("priceWithoutEquipment", labor);
                            const next = syncPackageTotals(
                              values.equipments,
                              labor
                            );
                            setFieldValue(
                              "priceWithEquipment",
                              next.priceWithEquipment > 0
                                ? next.priceWithEquipment
                                : ""
                            );
                          }}
                          {...onMakeFormikVariable("priceWithoutEquipment")}
                          helperText={
                            errors.priceWithoutEquipment &&
                            touched.priceWithoutEquipment
                              ? errors.priceWithoutEquipment
                              : "قیمت بدون تجهیزات — فقط دستمزد/اجرا"
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="قیمت تمام‌شده با تجهیزات (ریال)"
                          type="number"
                          value={
                            pricing.priceWithEquipment > 0
                              ? pricing.priceWithEquipment
                              : ""
                          }
                          InputProps={{ readOnly: true }}
                          helperText="جمع تجهیزات + دستمزد (بدون مالیات بر ارزش افزوده)"
                          fullWidth
                          variant="outlined"
                          name="priceWithEquipment"
                          error={
                            errors.priceWithEquipment &&
                            touched.priceWithEquipment
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="هزینه ارسال (ریال) *"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("deliveryFee")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <JalaliDatePicker
                          label="تاریخ اعتبار قیمت *"
                          size="small"
                          fullWidth
                          value={values.priceValidUntil || null}
                          onChange={(v) => setFieldValue("priceValidUntil", v)}
                          error={
                            Boolean(errors.priceValidUntil) &&
                            Boolean(touched.priceValidUntil)
                          }
                          helperText={
                            errors.priceValidUntil && touched.priceValidUntil
                              ? errors.priceValidUntil
                              : "قیمت پکیج تا این تاریخ معتبر است"
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            bgcolor: "action.hover",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            <Box component="span" fontWeight={700} color="text.primary">
                              خلاصه قیمت:{" "}
                            </Box>
                            تجهیزات: {formatMoneyFa(pricing.equipmentTotal)} — دستمزد:{" "}
                            {formatMoneyFa(pricing.labor)} — تمام‌شده (بدون مالیات):{" "}
                            {formatMoneyFa(pricing.priceWithEquipment)}
                            {deliveryForCustomer > 0
                              ? ` — با ارسال برای مشتری: ${formatMoneyFa(
                                  pricing.priceWithEquipment + deliveryForCustomer
                                )}`
                              : ""}
                          </Typography>
                          {pricing.priceWithEquipment > 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                              با مالیات بر ارزش افزوده ۱۰٪ (فقط نمایش — در قرارداد اضافه می‌شود):{" "}
                              <Box component="span" fontWeight={700} color="text.primary">
                                {formatMoneyFa(
                                  Math.round(Number(pricing.priceWithEquipment) * 1.1)
                                )}
                              </Box>
                              {deliveryForCustomer > 0
                                ? ` — با ارسال: ${formatMoneyFa(
                                    Math.round(
                                      (Number(pricing.priceWithEquipment) +
                                        Number(deliveryForCustomer)) *
                                        1.1
                                    )
                                  )}`
                                : ""}
                            </Typography>
                          ) : null}
                        </Box>
                      </Grid>

                      <Grid item xs={6} md={2}>
                        <TextField
                          label="مدت اجرا (روز)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("installationDurationDays")}
                        />
                      </Grid>

                      <Grid item xs={6} md={2}>
                        <TextField
                          label="مدت گارانتی (ماه)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("warrantyMonths")}
                        />
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <TextField
                          label="توضیحات"
                          multiline
                          minRows={2}
                          maxRows={4}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("description")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            "& img": { height: "72px !important", maxHeight: 72 },
                            "& > .MuiGrid-container": { minHeight: 0 },
                          }}
                        >
                          <FileUploader
                            moduleName={"SLN/Contractor/PackageManagement"}
                            maxFiles={1}
                            label="تصویر پکیج"
                            accept="image/*"
                            baseUrl={baseUrl}
                            onChange={(value) => {
                              const finalValue =
                                typeof value === "string" ? value : "";
                              setFieldValue("imageUrl", finalValue);
                            }}
                            placeholder="تصویر پکیج را انتخاب کنید"
                            {...onMakeFormikVariable("imageUrl")}
                          />
                        </Box>
                      </Grid>

                      {/* تجهیزات */}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={2}
                        >
                          <Typography variant="h6">تجهیزات پکیج</Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              setFieldValue("equipments", [
                                ...values.equipments,
                                {
                                  equipmentType: "",
                                  brand: "",
                                  model: "",
                                  quantity: 1,
                                  unitPrice: 0,
                                  totalPrice: 0,
                                  specifications: "",
                                  warrantyPeriod: "",
                                  description: "",
                                  imageUrl: "",
                                  equipmentItemId: null,
                                  publicCode: "",
                                  masterPicker: null,
                                },
                              ])
                            }
                          >
                            افزودن تجهیز
                          </Button>
                        </Box>
                      </Grid>

                      {values.equipments?.map((equipment, index) => {
                        const isMandatoryCatalog = requiresCatalogSelection(
                          equipment.equipmentType
                        );
                        const catalogMissing =
                          isMandatoryCatalog && !equipment.equipmentItemId;

                        return (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ p: 1.5 }}>
                            <Grid container spacing={1.5} alignItems="flex-start">
                              <Grid item xs={12} sm={4} md={3}>
                                <FormControl
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  error={!equipment.equipmentType && touched.equipments}
                                >
                                  <InputLabel>نوع تجهیز *</InputLabel>
                                  <Select
                                    value={equipment.equipmentType}
                                    label="نوع تجهیز *"
                                    onChange={(e) => {
                                      const newEquipments = [
                                        ...values.equipments,
                                      ];
                                      const nextType = e.target.value;
                                      const prevType =
                                        newEquipments[index].equipmentType;
                                      newEquipments[index].equipmentType = nextType;
                                      if (String(prevType) !== String(nextType)) {
                                        newEquipments[index].masterPicker = null;
                                        newEquipments[index].equipmentItemId = null;
                                        newEquipments[index].publicCode = "";
                                        newEquipments[index].brand = "";
                                        newEquipments[index].model = "";
                                      }
                                      setFieldValue("equipments", newEquipments);
                                    }}
                                  >
                                    {equipmentTypes.map((type) => (
                                      <MenuItem
                                        key={type.value}
                                        value={type.value}
                                      >
                                        {type.label || type.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {!equipment.equipmentType && touched.equipments && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                      نوع تجهیز الزامی است
                                    </Typography>
                                  )}
                                </FormControl>
                              </Grid>

                              <Grid item xs={10} sm={7} md={8}>
                                <EquipmentItemPicker
                                  label="انتخاب از کاتالوگ مرجع"
                                  required
                                  disabled={!equipment.equipmentType}
                                  equipmentTypeEnum={
                                    equipment.equipmentType || null
                                  }
                                  error={catalogMissing && touched.equipments}
                                  helperText={
                                    !equipment.equipmentType
                                      ? "ابتدا نوع تجهیز را انتخاب کنید"
                                      : catalogMissing && touched.equipments
                                      ? MANDATORY_CATALOG_MESSAGE
                                      : undefined
                                  }
                                  allowRequestNew
                                  submitNewRequest={
                                    ContractorEquipmentCatalogApi.submitNewEquipmentRequest
                                  }
                                  value={equipment.masterPicker}
                                  onChange={(picked) => {
                                    const newEquipments = [...values.equipments];
                                    newEquipments[index].masterPicker = picked;
                                    newEquipments[index].equipmentItemId =
                                      picked?.equipmentItemId || null;
                                    newEquipments[index].publicCode =
                                      picked?.publicCode || "";
                                    if (picked?.brandName)
                                      newEquipments[index].brand = picked.brandName;
                                    if (picked?.modelName)
                                      newEquipments[index].model = picked.modelName;
                                    if (!picked) {
                                      newEquipments[index].brand = "";
                                      newEquipments[index].model = "";
                                    }
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={2} sm={1} md={1} sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}>
                                <IconButton
                                  color="error"
                                  size="small"
                                  aria-label="حذف تجهیز"
                                  onClick={() => {
                                    const newEquipments = values.equipments.filter(
                                      (_, i) => i !== index
                                    );
                                    updateEquipmentsAndTotals(newEquipments);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Grid>

                              {isMandatoryCatalog && (equipment.brand || equipment.model) ? (
                                <Grid item xs={12} sx={{ pt: "0 !important" }}>
                                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="caption" color="text.secondary">
                                      {[equipment.brand, equipment.model].filter(Boolean).join(" · ")}
                                    </Typography>
                                    {equipment.publicCode ? (
                                      <EquipmentPublicCodeChip code={equipment.publicCode} />
                                    ) : null}
                                  </Box>
                                </Grid>
                              ) : null}

                              {!isMandatoryCatalog ? (
                                <>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="برند *"
                                      value={equipment.brand}
                                      error={!equipment.brand && touched.equipments}
                                      helperText={
                                        !equipment.brand && touched.equipments
                                          ? "برند الزامی است"
                                          : undefined
                                      }
                                      onChange={(e) => {
                                        const newEquipments = [
                                          ...values.equipments,
                                        ];
                                        newEquipments[index].brand = e.target.value;
                                        setFieldValue("equipments", newEquipments);
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="مدل *"
                                      value={equipment.model}
                                      error={!equipment.model && touched.equipments}
                                      helperText={
                                        !equipment.model && touched.equipments
                                          ? "مدل الزامی است"
                                          : undefined
                                      }
                                      onChange={(e) => {
                                        const newEquipments = [
                                          ...values.equipments,
                                        ];
                                        newEquipments[index].model = e.target.value;
                                        setFieldValue("equipments", newEquipments);
                                      }}
                                    />
                                  </Grid>
                                </>
                              ) : null}

                              <Grid item xs={6} sm={3} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="تعداد"
                                  type="number"
                                  value={equipment.quantity}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    const quantity = parseInt(e.target.value) || 0;
                                    newEquipments[index].quantity = quantity;
                                    newEquipments[index].totalPrice =
                                      quantity * (newEquipments[index].unitPrice || 0);
                                    updateEquipmentsAndTotals(newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={6} sm={3} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="قیمت واحد (ریال)"
                                  type="number"
                                  value={equipment.unitPrice}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    const unitPrice = parseFloat(e.target.value) || 0;
                                    newEquipments[index].unitPrice = unitPrice;
                                    newEquipments[index].totalPrice =
                                      unitPrice * (newEquipments[index].quantity || 0);
                                    updateEquipmentsAndTotals(newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={6} sm={3} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="قیمت کل (ریال)"
                                  type="number"
                                  value={equipment.totalPrice}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>

                              <Grid item xs={6} sm={3} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="گارانتی (ماه)"
                                  type="number"
                                  value={equipment.warrantyPeriod}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    newEquipments[index].warrantyPeriod = e.target.value;
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={8}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="توضیحات و مشخصات فنی"
                                  multiline
                                  minRows={2}
                                  maxRows={4}
                                  value={equipment.description}
                                  onChange={(e) => {
                                    const newEquipments = [...values.equipments];
                                    newEquipments[index].description = e.target.value;
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                  placeholder="مثال: پنل مونو ۵۵۰ وات، بازدهی ۲۱٪، گارانتی ۲۵ ساله"
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <Box
                                  sx={{
                                    "& img": { height: "72px !important", maxHeight: 72 },
                                  }}
                                >
                                  <FileUploader
                                    moduleName={"SLN/Contractor/PackageManagement"}
                                    maxFiles={1}
                                    label="تصویر تجهیز"
                                    accept="image/*"
                                    baseUrl={baseUrl}
                                    onChange={(value) => {
                                      const newEquipments = [...values.equipments];
                                      newEquipments[index].imageUrl = typeof value === "string" ? value : "";
                                      setFieldValue("equipments", newEquipments);
                                    }}
                                    placeholder="تصویر تجهیز را انتخاب کنید"
                                    value={equipment.imageUrl}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                        );
                      })}

                      {/* ویژگی‌ها */}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={2}
                        >
                          <Typography variant="h6">ویژگی‌های پکیج</Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              setFieldValue("features", [
                                ...values.features,
                                {
                                  featureTitle: "",
                                  featureValue: "",
                                  description: "",
                                  displayOrder: values.features.length + 1,
                                },
                              ])
                            }
                          >
                            افزودن ویژگی
                          </Button>
                        </Box>
                      </Grid>

                      {values.features?.map((feature, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12}>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography variant="subtitle1">
                                    ویژگی {index + 1}
                                  </Typography>
                                  <IconButton
                                    color="error"
                                    onClick={() => {
                                      const newFeatures =
                                        values.features.filter(
                                          (_, i) => i !== index
                                        );
                                      setFieldValue("features", newFeatures);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="عنوان ویژگی *"
                                  value={feature.featureTitle}
                                  error={!feature.featureTitle && touched.features}
                                  helperText={!feature.featureTitle && touched.features ? "عنوان ویژگی الزامی است" : ""}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].featureTitle =
                                      e.target.value;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label="مقدار ویژگی *"
                                  value={feature.featureValue}
                                  error={!feature.featureValue && touched.features}
                                  helperText={!feature.featureValue && touched.features ? "مقدار ویژگی الزامی است" : ""}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].featureValue =
                                      e.target.value;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={2}>
                                <TextField
                                  fullWidth
                                  label="ترتیب"
                                  type="number"
                                  value={feature.displayOrder}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].displayOrder =
                                      parseInt(e.target.value) || 0;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="توضیحات ویژگی"
                                  multiline
                                  rows={2}
                                  value={feature.description}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].description =
                                      e.target.value;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}

                      {/* دکمه‌ها */}
                      <Grid
                        item
                        xs={12}
                        container
                        justifyContent="flex-end"
                        spacing={2}
                        mt={2}
                      >
                        <Grid item>
                          <Button
                            disabled={isSubmitting || createUpdateLoading}
                            variant="outlined"
                            size="large"
                            onClick={onNavigateToListPage}
                          >
                            انصراف
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            disabled={isSubmitting || createUpdateLoading}
                            variant="contained"
                            type="submit"
                            size="large"
                          >
                            {createUpdateLoading && (
                              <CircularProgress
                                size={16}
                                color="inherit"
                                sx={{ mr: 1 }}
                              />
                            )}
                            {isEditMode ? "ویرایش پکیج" : "ایجاد پکیج"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PackageModifyPage;
